import type {
  AddKatanaAccountDto,
  HubUserCredentials,
  KatanaDelegationAuthorizeBodyDto,
  KatanaDelegationPrepareBodyDto,
  KatanaDelegationPrepareResultDto,
} from "@cede/types";
import { API_ENDPOINT } from "@cede/utils";
import type { ApiHelper } from "@cede/utils";

/**
 * Phase 1: BE validates HMAC against Katana, mints a delegated keypair, and
 * returns a wagmi-ready EIP-712 typed-data envelope for the user to sign.
 */
export async function prepareKatanaDelegation(
  apiHelper: ApiHelper,
  auth: HubUserCredentials,
  params: KatanaDelegationPrepareBodyDto,
): Promise<KatanaDelegationPrepareResultDto> {
  const response = await apiHelper.postAuth(auth, API_ENDPOINT.EXCHANGE_ACCOUNT_KATANA_DELEGATION_PREPARE, {
    bodyParams: params,
  });
  return response as KatanaDelegationPrepareResultDto;
}

/**
 * Phase 2: BE forwards the signature to Katana's `/v1/delegatedKeys`, encrypts
 * the delegated private key in SSM, and returns `{ accountId, walletAddress, status }`.
 */
export async function authorizeKatanaDelegation(
  apiHelper: ApiHelper,
  auth: HubUserCredentials,
  params: KatanaDelegationAuthorizeBodyDto,
): Promise<AddKatanaAccountDto> {
  const response = await apiHelper.postAuth(auth, API_ENDPOINT.EXCHANGE_ACCOUNT_KATANA_DELEGATION_AUTHORIZE, {
    bodyParams: params,
  });
  return response as AddKatanaAccountDto;
}

/**
 * Reshape the prepare response into wagmi's `signTypedData` argument. The BE
 * already shapes `eip712TypedData` for wagmi; this only narrows the address
 * fields from `string` (zod-regex-validated) to `0x${string}` for viem.
 */
export function formatKatanaSignTypedData(prepare: KatanaDelegationPrepareResultDto) {
  const td = prepare.eip712TypedData;
  return {
    domain: {
      name: td.domain.name,
      version: td.domain.version,
      chainId: td.domain.chainId,
      verifyingContract: td.domain.verifyingContract as `0x${string}`,
    },
    types: td.types,
    primaryType: td.primaryType,
    message: {
      nonce: td.message.nonce,
      delegatedPublicKey: td.message.delegatedPublicKey as `0x${string}`,
      message: td.message.message,
    },
  } as const;
}
